import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "@auth/frontend";

// Layouts
import { Layout } from "./pages/layout";
import { UserLayout } from "@agentic_conversation/layouts/user-layout";

// Agentic system pages
import { Agents } from "@agentic_system/pages/agents";
import { Tools } from "@agentic_system/pages/tools";
import { Files } from "@agentic_system/pages/files";
import { Logs } from "@agentic_system/pages/logs";
import { SystemPromptLayout } from "@agentic_system/pages/system-prompt/layout";
import { SystemPromptEditor } from "@agentic_system/pages/system-prompt/editor";
import { AgentDetail } from "@agentic_system/pages/agents/agent-detail";
import { SchemaBuilderPage } from "@agentic_system/pages/agents/schema-builder-page";

// Conversation pages
import { Impersonate } from "@agentic_conversation/pages/impersonate";
import { Conversations } from "@agentic_conversation/pages/conversations";
import { Chat } from "@agentic_conversation/pages/chat";

// UI components
import { NotFound } from "@ui/components/not-found";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function LocationLogger() {
  const location = useLocation();
  useEffect(() => {
    console.log("React Router location:", location.pathname);
  }, [location]);
  return null;
}

export function Application() {
  console.log("Application mounting, current path:", window.location.pathname);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <LocationLogger />
          <Routes>
            {/* System admin routes */}
            <Route path="/system/agentic" element={<Layout />}>
              <Route index element={<Logs />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="conversation/:id" element={<Chat />} />
              <Route path="agents" element={<Navigate to="connected-user" replace />} />
              <Route path="agents/connected-user" element={<Agents systemPromptType="connected_user" />} />
              <Route path="agents/profile-entrypoint" element={<Agents systemPromptType="profile_entrypoint" />} />
              <Route path="agents/:id" element={<AgentDetail />} />
              <Route path="agents/:id/schema" element={<SchemaBuilderPage />} />
              <Route path="agents/:id/schema/:versionId" element={<SchemaBuilderPage />} />
              <Route path="system-prompt" element={<SystemPromptLayout />}>
                <Route index element={<Navigate to="connected-user" replace />} />
                <Route path="connected-user" element={<SystemPromptEditor promptType="connected_user" />} />
                <Route path="profile-entrypoint" element={<SystemPromptEditor promptType="profile_entrypoint" />} />
              </Route>
              <Route path="tools" element={<Tools />} />
              <Route path="files" element={<Files />} />
              <Route path="logs" element={<Logs />} />
            </Route>

            {/* User chat routes */}
            <Route path="/app/conversations" element={<UserLayout />}>
              <Route path=":id" element={<Chat />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="bottom-right" />
      <div id="headlessui-portal-root"></div>
    </QueryClientProvider>
  );
}

export default Application;
